<script setup lang="ts">
const { t } = useI18n()

// The routes the app serves (arrival at Zvartnots only), each with a typical
// basket that the slider starts from.
const routes = [
  { label: 'DXB → EVN', basketEUR: 600 },
  { label: 'MXP → EVN', basketEUR: 500 },
  { label: 'TBS → EVN', basketEUR: 300 },
]

const orders    = ref(3)   // 1..5: the most a faithful's level allows on one flight
const routeIdx  = ref(0)
const basketEUR = ref(routes[0].basketEUR)

const route = computed(() => routes[routeIdx.value])
function pickRoute(i: number) {
  routeIdx.value = i
  basketEUR.value = routes[i].basketEUR
}

// The faithful's fee (User Agreement §8.7): 5% of the goods price, or 5/14 of
// the fixed €50 service fee charged when the goods total is below €500.
const perOrder = computed(() => basketEUR.value >= 500 ? basketEUR.value * 0.05 : 50 * 5 / 14)
const total    = computed(() => perOrder.value * orders.value)
const eur = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(',', '.').replace(/\u202f|\u00a0/g, ' ')
</script>

<template>
  <section id="earnings" class="rod-section relative">
    <div class="rod-container">
      <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
        <div class="rod-eyebrow mb-5">💰 {{ t('calc.eyebrow') }}</div>
        <h2 class="rod-h2 mb-5" v-html="t('calc.title')" />
        <p class="text-lg text-[var(--color-text-muted)]">{{ t('calc.lead') }}</p>
      </div>

      <div class="rod-card rod-card--glow grid lg:grid-cols-[1.1fr_0.9fr] gap-10 p-8 lg:p-12" data-reveal>
        <!-- Inputs -->
        <div class="flex flex-col gap-7">
          <!-- Route -->
          <div>
            <div class="flex justify-between mb-3">
              <span class="text-sm text-[var(--color-text-muted)] uppercase tracking-wider">{{ t('calc.routeLabel') }}</span>
              <span class="font-mono font-bold text-amber-300">{{ route.label }}</span>
            </div>
            <div class="flex gap-2 flex-wrap">
              <button
                v-for="(r, i) in routes" :key="r.label"
                @click="pickRoute(i)"
                class="px-3 py-2 rounded-lg text-sm font-mono transition-colors"
                :class="routeIdx === i ? 'bg-amber-500/90 text-[#0A0A0F]' : 'bg-white/5 text-[var(--color-text-muted)] hover:bg-white/10'"
              >{{ r.label }}</button>
            </div>
          </div>

          <!-- Orders count -->
          <div>
            <div class="flex justify-between mb-3">
              <span class="text-sm text-[var(--color-text-muted)] uppercase tracking-wider">{{ t('calc.ordersLabel') }}</span>
              <span class="font-display font-bold text-amber-300 text-2xl">{{ orders }}</span>
            </div>
            <input
              type="range" min="1" max="5" step="1" v-model.number="orders"
              :aria-label="t('calc.ordersLabel')"
              :aria-valuetext="`${orders}`"
              class="w-full h-2 rounded-full appearance-none bg-white/10 accent-amber-400"
            />
            <div class="flex justify-between text-xs text-[var(--color-text-dim)] mt-1">
              <span>1</span><span>5</span>
            </div>
          </div>

          <!-- Average basket -->
          <div>
            <div class="flex justify-between mb-3">
              <span class="text-sm text-[var(--color-text-muted)] uppercase tracking-wider">{{ t('calc.basketLabel') }}</span>
              <span class="font-display font-bold text-amber-300 text-2xl">€{{ basketEUR.toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ') }}</span>
            </div>
            <input
              type="range" min="100" max="2000" step="50" v-model.number="basketEUR"
              :aria-label="t('calc.basketLabel')"
              :aria-valuetext="`€${basketEUR}`"
              class="w-full h-2 rounded-full appearance-none bg-white/10 accent-amber-400"
            />
            <div class="flex justify-between text-xs text-[var(--color-text-dim)] mt-1">
              <span>€100</span><span>€2 000</span>
            </div>
          </div>
        </div>

        <!-- Output -->
        <div class="flex flex-col items-center justify-center text-center bg-gradient-to-br from-amber-500/15 to-emerald-500/10 rounded-2xl p-8 border border-amber-500/20">
          <div class="text-sm text-[var(--color-text-muted)] uppercase tracking-wider mb-4">
            {{ t('calc.earnedTitle') }}
          </div>
          <div class="font-display font-bold rod-gradient-text mb-3" style="font-size: clamp(3rem, 7vw, 5.5rem); line-height: 1; letter-spacing: -0.03em;">
            €{{ eur(total) }}
          </div>
          <div class="text-sm text-[var(--color-text-dim)]">
            €{{ eur(perOrder) }} {{ t('calc.earnedPerOrder') }}
          </div>

          <a href="#cta" class="rod-btn-primary mt-8" style="font-size: 14px;">
            {{ t('hero.ctaCourier') }}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FBBF24, #F59E0B);
  cursor: pointer;
  box-shadow: 0 0 0 4px rgba(245,158,11,0.18), 0 4px 12px rgba(245,158,11,0.4);
}
input[type="range"]::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #FBBF24, #F59E0B);
  cursor: pointer;
  box-shadow: 0 0 0 4px rgba(245,158,11,0.18), 0 4px 12px rgba(245,158,11,0.4);
}
</style>
